import {useEffect} from "react";
import {useLocation} from "react-router-dom";

import {RouterUtil} from "base/navigation/utilities";
import {useErrors} from "base/error/provider";
import {useNavigateWithReload} from "base/navigation/hooks";
import {useFormDirtyContext} from "base/form/provider";
import {
    usePermitExtensionProcess,
    usePermitExtensionProcessUrl,
    usePermitExtensionSteps,
} from "permitextension/hooks";

import {HorizontalStepper} from "base/stepper/components";
import {PermitExtensionSummary} from "permitextension/presentational";
import {PermitExtensionService} from "permitextension/service";
import Stack from "@mui/material/Stack";

// ------TODO(cmartin): merge with permit application widget -----
const ViewPermitExtensionWidget = ({entity, process}) => {
    const navigate = useNavigateWithReload();
    const location = useLocation();
    const basePath = `/permits/extensions/list/${entity.id}`;
    const stepPath = RouterUtil.getLastUrlSegment(location);

    const {completedSteps, updateCurrentStep, isStepEditable} =
        usePermitExtensionProcess(entity);
    const {steps} = usePermitExtensionSteps(entity, isStepEditable);
    const {getStepPath} = usePermitExtensionProcessUrl(entity);
    const isProcessRevertible = true; // TODO(cmartin): handle this if field inspection is implemented for permit extensions
    const {isFormDirty} = useFormDirtyContext();
    const {handleErrors, clearErrors} = useErrors();

    const activeStepIndex = steps.findIndex(step => step.path === stepPath);
    const isFinalStep = steps[activeStepIndex]?.order === steps.length - 1;

    useEffect(() => {
        clearErrors();
        if (entity) updateCurrentStep(entity.status);
    }, [entity, location.state?.lastRefreshDate]);

    const handleChangeStep = step => {
        navigate(`${basePath}/${process}/${step.path}`);
    };

    const handleCompleteStep = () => {
        PermitExtensionService.updateStatus(entity.id, {data: "next"})
            .then(updatedPermit => {
                updateCurrentStep(updatedPermit.status);
                const processPath = getStepPath(updatedPermit, basePath);
                navigate(`${processPath}`, true);
            })
            .catch(error => {
                handleErrors(error);
            });
    };

    const handleRevertStep = () => {
        PermitExtensionService.updateStatus(entity.id, {data: "previous"})
            .then(updatedPermit => {
                updateCurrentStep(updatedPermit.status);
                const stepPath = getStepPath(updatedPermit, basePath);
                navigate(`${stepPath}`, true);
            })
            .catch(error => {
                handleErrors(error);
            });
    };

    const handelCancelProcess = () => {
        PermitExtensionService.updateStatus(entity.id, {data: "denied"})
            .then(updatedPermit => {
                updateCurrentStep(updatedPermit.status);
                navigate(`/permits/extensions/list`);
            })
            .catch(error => {
                handleErrors(error);
            });
    };

    const handelApproveProcess = () => {
        PermitExtensionService.updateStatus(entity.id, {data: "approved"})
            .then(updatedPermit => {
                updateCurrentStep(updatedPermit.status);
                navigate(`/permits/approved/list/${entity.permit.id}/summary`);
            })
            .catch(error => {
                handleErrors(error);
            });
    };

    const displayAlert = () => {
        return (
            steps?.length &&
            steps[activeStepIndex] &&
            steps[activeStepIndex]?.uncompleteFields.length
        );
    };

    const disableCompleteButton = entity?.technical_decision === "Negativo";
    const disableBackButton = !isProcessRevertible;

    return entity && steps ? (
        <Stack spacing={1}>
            <PermitExtensionSummary entity={entity} basePath={basePath} />
            <HorizontalStepper
                steps={steps}
                activeStepIndex={activeStepIndex}
                completedSteps={completedSteps}
                disableNavigation={!isStepEditable || isFormDirty}
                disableCompleteButton={disableCompleteButton}
                disableBackButton={disableBackButton}
                displayAlert={displayAlert()}
                onChangeStep={handleChangeStep}
                onCompleteStep={handleCompleteStep}
                onRevertStep={handleRevertStep}
                onCancelProcess={handelCancelProcess}
                onApproveProcess={isFinalStep ? handelApproveProcess : null}
            />
        </Stack>
    ) : null;
};

export default ViewPermitExtensionWidget;
